import React, { createContext, useState, useEffect, useMemo } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { WiMuleSQL } from "../services/WiMuleSQL";
import { isCryptoAvailable } from "../services/crypto";
import { PublicClientApplication } from "@azure/msal-browser";
import {  
  loginRequestREMA,
  msalREMA,
} from "../modules/authentication/entra/entraConfig";
import { fetchUserData } from "../modules/authentication/entra/fetchUserData";
import { resolveLocation } from "../config/resolveLocation";
import { detectDevice } from "../config/deviceInfo";

export const UserContext = createContext();

const createMsalInstance = () => {
  return isCryptoAvailable() && import.meta.env.VITE_USE_ENTRA_AUTH === "true"
    ? new PublicClientApplication(msalREMA)
    : null;
};

export const UserProvider = ({ children }) => {
  const initialUserDetails = useMemo(
    () => ({
      userId: null,
      userEmail: null,
      userToken: null,
      userName: null,
      userCompany: null,
      userDepartment: null,
      userPosition: null,
      userSkillSet: null,
      userAccessLevel: null,
      userImage: null,
      userWiMuleToken: null,
      location: null,
      userWiMuleTokenTimestamp: null,
      isMobile: null,
      mobileOS: null,
    }),
    []
  );

  

  const [userDetails, setUserDetails] = useState(initialUserDetails);
  const [msalInstance] = useState(createMsalInstance);

  const isLocation = (...locations) => locations.includes(userDetails.location);

  const fetchUserDetails = async (email) => {
    try {
      const response = await fetch(
        `${WiMuleSQL}/api/Auth/GetUserAccountData?email=${email}`
      );
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }
      const { data } = await response.json();
      if (data) {
        setUserDetails((prev) => ({
          ...prev,
          userId: data.id ?? prev.userId,
          userEmail: data.email ?? prev.userEmail,
          userName: data.userName ?? prev.userName,
          userCompany: data.company ?? prev.userCompany,
          userDepartment: data.department ?? prev.userDepartment,
          userPosition: data.position ?? prev.userPosition,
          userSkillSet: data.skillSet ?? prev.userSkillSet,
          userAccessLevel: data.accessLevel ?? prev.userAccessLevel,
          userImage: data.userImageBase64 || prev.userImage,
          userWiMuleToken: prev.userWiMuleToken,
          userWiMuleTokenTimestamp: prev.userWiMuleTokenTimestamp,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  };

  const restoreSessions = async () => {
    const location = await resolveLocation();
     const { isMobile, mobileOS } = detectDevice();

    setUserDetails((prev) => ({
      ...prev,
      location,
      isMobile,
      mobileOS
    }));

    console.log("Resolved location:", location);
  };

  useEffect(() => {
    const initializeRuntime = async () => {
      await restoreSessions();
      const auth = getAuth();
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          const email = user.email;
          const token = await user.getIdToken();
          console.log("🔹 Restoring Firebase session:", email);

          setUserDetails((prev) => ({
            ...prev,
            userEmail: email,
            userToken: token,
            userWiMuleToken: prev.userWiMuleToken,
          }));

          await fetchUserDetails(email);
        } else {
          console.log("❌ No Firebase session found.");
        }
      });

      if (msalInstance) {
        try {
          await msalInstance.initialize();
          const accounts = msalInstance.getAllAccounts();

          if (accounts.length > 0) {
            console.log(
              "🔹 Restoring Entra session for:",
              accounts[0].username
            );

            const response = await msalInstance.acquireTokenSilent({
              scopes: loginRequestREMA.scopes,
              account: accounts[0],
            });

            const payload = JSON.parse(atob(response.idToken.split(".")[1]));
            const email = payload.preferred_username || payload.email;

            if (email) {
              const userDataResponse = await fetchUserData(email);
              const userData = userDataResponse?.data?.userAuth;

              setUserDetails((prev) => ({
                ...prev,
                userEmail: userData.email,
                userName: userData.userName,
                userCompany: userData.company,
                userAccessLevel: userData.accessLevel,
                userImage: userData.image?.imageData
                  ? `data:image/png;base64,${userData.image.imageData}`
                  : null,
                userWiMuleToken: userDataResponse.data.wiMuleToken,
                userWiMuleTokenTimestamp: userDataResponse.data.wiMuleTokenTimestamp,
              }));

            }
          } else {
            console.log("🔹 No Entra session to restore.");
          }
        } catch (error) {
          console.warn("❌ Failed to restore Entra session:", error);
        }
      }

      return () => unsubscribe();
    };

    initializeRuntime();
  }, [setUserDetails, msalInstance]);

  return (
    <UserContext.Provider
      value={{
        userDetails,
        setUserDetails,
        msalInstance,
        isLocation,
        location: userDetails.location,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
